import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { apiCache, dashboardCache, layoutCache } from '@processo/shared/components/entity-layout/smart-table/cache';
import { EntityDashboardServiceService } from '@processo/shared/service/entity-dashboard-service.service';
import moment from 'moment';
import { environment } from 'projects/processo/src/environments/environment';
import { BehaviorSubject, map, of, Subject, tap } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';

@Injectable(
  {providedIn: 'root'}
)
export class OrgDashboardService {
  private _dashboardConfig = signal({
    tabChange: false,
    resize: false,
    positionChange: false,
    removeWidget: false,
    previewMode:false
  });

  private _widgetsRefreshed = signal<string[]>([]);
  widgetsRefreshed = this._widgetsRefreshed.asReadonly();
  setWidgetsRefreshed(widgetId: string) {
    this._widgetsRefreshed.update(prev => [...prev, widgetId]);
  }
  clearWidgetsRefreshed(widgetId: string) { 
    this._widgetsRefreshed.update(prev => prev.filter(id => id !== widgetId));
  }
  resetWidgetsRefreshed() {
    this._widgetsRefreshed.set([]);
  }
  private filterStateMap = new Map<string, any[]>();
  private lastEntityKey: string | null = null;
  //signal to know if we are in a dashboard view.
  private isInDashboardContext = signal(false);
  // Store pending entity filters that need to be applied when dashboard loads
  private pendingEntityFilters = new Map<string, any[]>();

  public drilldownKey: string | null = null;
  
  //Public method for components to update the context.
  setDashboardContext(inContext: boolean) {
    this.isInDashboardContext.set(inContext);
  }

  storeFilterState(key: string, filters: any[]) {
    this.filterStateMap.set(key, filters);
    this._filterState.set(filters);
    try {
      sessionStorage.setItem(`dbf::${key}`, JSON.stringify(filters));
      const current = this.currentDashBoard();
      const meta = { org_id: current?.org_id, process_id: current?.process_id, org_process_did: current?.org_process_did };
      sessionStorage.setItem(`dbf::last`, JSON.stringify({ key, meta, filters }));
    } catch {}
  }
  
  getStoredFilterState(key: string): any[] {
    const inMemory = this.filterStateMap.get(key) || [];
    if (inMemory.length > 0) return inMemory;
    try {
      const raw = sessionStorage.getItem(`dbf::${key}`);
      if (raw) return JSON.parse(raw);
      
    } catch {}
    return [];
  }

  getConfig = computed(() => this._dashboardConfig());
  private _filterState = signal<any[]>([]);
  filterState = this._filterState.asReadonly();

  updateFilterState(filters: any[]) {
    this._filterState.set(filters);
    // When dashboard filters are actively updated, un-suppress entity filters
    try {
      const hasAny = Array.isArray(filters) && filters.some((f: any) => {
        const v = f?.default;
        return !(v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0));
      });
      if (hasAny) {
        layoutCache.set('filtersSuppressed', false);
      }
    } catch {}
  }


  
  getFilterState(): any[] {
    return this._filterState();
  }
  
  checkAndClearFiltersOnEntityChange(currentEntityKey: string) {
    const currentEntityPart = this.extractEntityPart(currentEntityKey);
    const lastEntityPart = this.lastEntityKey ? this.extractEntityPart(this.lastEntityKey) : null;
    
    if (lastEntityPart && currentEntityPart !== lastEntityPart && !this.drilldownKey) {
      //console.log('Entity changed - clearing all filter states');
      this.filterStateMap.clear();
      this._filterState.set([]);
      // NOTE: We intentionally do NOT clear pendingEntityFilters here
      // as they should persist across entity changes for bidirectional sync
    }
    
    this.lastEntityKey = currentEntityKey;
  }
  
  private extractEntityPart(dashboardKey: string): string {
    const parts = dashboardKey.split('_');
    if (parts.length >= 2) {
      return `${parts[0]}_${parts[1]}`; // org_id_process_id
    }
    return dashboardKey;
  }
  
  clearInMemoryFilterState(key: string) {
    //console.log(`Clearing in-memory filter state for key: ${key}`);
    this.filterStateMap.delete(key);
  }
  

  setConfig(config: Partial<{
    tabChange: boolean, 
    resize: boolean, 
    positionChange: boolean, 
    removeWidget: boolean,
    previewMode:boolean
  }>) {
    this._dashboardConfig.update(currentConfig => ({
      ...currentConfig,
      ...config
    }));
  }

  resetConfig() {
    this._dashboardConfig.set({
      tabChange: false,
      resize: false,
      positionChange: false,
      removeWidget: false,
      previewMode:true
    });
  }
  
  constructor(private http: HttpClient) { }
  currentDashBoard = signal<any>(null)
  selectedTab = signal(1)
  getCurrentTab=computed(()=>this.selectedTab()-1)
  openWidgetSlider = false;
  openWidgetfilter = false;
  //isDraggingFromWidgetPanel = false;
  update_query = new BehaviorSubject<any>(null);
  private inFlightRequests = new Map<string, any>();

  getQueryFetch(body: any) {
    const stringifyBody = JSON.stringify(body);
    
    if (dashboardCache.has(stringifyBody)) {
      return of(dashboardCache.get(stringifyBody));
    }

    if (this.inFlightRequests.has(stringifyBody)) {
      return this.inFlightRequests.get(stringifyBody);
    }

    const request$ = this.http
      .post(`${environment.base_eru_routes_url}/processo/func/query_fetch`, body)
      .pipe(
        tap((response) => {
          dashboardCache.set(stringifyBody, response);
          this.update_query.next(stringifyBody);
        }),
        finalize(() => {
          this.inFlightRequests.delete(stringifyBody);
        }),
        shareReplay(1)
      );

    this.inFlightRequests.set(stringifyBody, request$);
    return request$;
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

   sortTabs<T extends { tab_idx: number }>(tabs: T[]): T[] {
    return tabs.sort((a, b) => a.tab_idx - b.tab_idx);
  }

  fetchDashboardList(body: {
    "org_id": string,
    "process_id": string
  }) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/fetch_dashboard_list`, body);
  }
  thousandsFormatter(value:number) {
    if (value >= 1_000_00) return (value / 1_000_00).toFixed(1) + ' L';
    if (value >= 1_000) return (value / 1_000).toFixed(1) + ' K';
    return value;
  }
  applyCustomFormatters(option:any) {
    if (option.yAxis?.axisLabel?.useThousandsFormatter) {
      option.yAxis.axisLabel.formatter = (value: number) => this.thousandsFormatter(value);
    }
  
    if (option.series) {
      const seriesArray = Array.isArray(option.series) ? option.series : [option.series];
      seriesArray.forEach((series: any, idx: number) => {
        if (series?.label?.useThousandsFormatter) {
          series.label.formatter = (params: any) => {
            const value = Array.isArray(params?.value)
              ? params.value[1]
              : (typeof params?.value === 'object' && params?.value !== null && 'y' in params.value)
                ? (params.value as any).y
                : params?.value;
            return this.thousandsFormatter(Number(value));
          };
        }
      });
      option.series = seriesArray;
    }
    // Repeat for other axes or series if needed
    return option;
  }
  dashboard_fetch(body: { did: string }) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/dashboard_fetch`, body).pipe(
      map(r => ({
        ...r,
        dashboard: {
          ...r.dashboard,
          tabs: this.sortTabs(r.dashboard.tabs),
          filters: this.filterSchema(r.dashboard.filters),
        },
      })),


    );
  }
  
  async update_default_filter(data:any,data_id:any){
    const filter_data = await Promise.all(
      data?.map(async (_filter: any) => {
        if (_filter.default_first_value && (_filter.type === 'dropdown_single_select' || _filter.type === 'dropdown_multi_select')) {
          
          // ✅ NEW: Check if this filter depends on another filter
          if (_filter.df) {
            const parentFilter = data.find((f: any) => f.name === _filter.df);
            if (!parentFilter?.default || 
                parentFilter.default === '' || 
                parentFilter.default === null ||
                (Array.isArray(parentFilter.default) && parentFilter.default.length === 0)) {
              //console.log(`⏭️ Skipping ${_filter.name} in update_default_filter because parent ${_filter.df} is empty`);
              return _filter; // Return filter without fetching default value
            }
          }
          
          _filter.default = await this.fetch_dashboard_data(_filter, data_id);
        }
        return _filter;
      })
    );
    return filter_data
  }
  
  dashboart_service = inject(EntityDashboardServiceService);
  fetch_dashboard_data(filter: any, data_id: any): Promise<any> {
    return new Promise((resolve) => {
      const body = {
        query: filter.query,
        transform: filter.transform,
        field_str: '',
        org_id: data_id?.org_id,
        process_id: data_id?.process_id,
      };
  
      this.dashboart_service.fetch_dashboard_data(body).subscribe({
        next: (res: any) => {
          let optionList = Array.isArray(res) ? res : [res];
          resolve(optionList[0][filter.name]);
        },
        error: () => {
          resolve(filter?.default);
        },
      });
    });
  }
  
  saveDashboardNew(body: { 
    "org_id": string, 
    "process_id": string 
  }) {
    //console.log('body',body);
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/save_dashboard`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }
  
  saveDashboardEditFilter(body:any) {
    //console.log('body',body);
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/save_dashboard`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  saveDashboardEdit(body: any) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/save_dashboard_widget`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  removeDashboardWidget(body: any) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/remove_dashboard_widget`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  saveDashboardWidget(body: any) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/save_dashboard_widget`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  removeDashboard(body: { "org_id": string, "process_id": string, "org_process_did": string }) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/remove_dashboard`, body);
  }

  saveDashboardTab(body: { "org_process_did": string, "tabs": Array<{ "tab_id": string, "tab_idx": number, "tab_name": string }> }) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/save_dashboard_tab`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs) } }))
    );
  }

  removeDashboardTab(body: { "org_process_did": string, "tab_id": string }) {
    return this.http.post<any>(`${environment.base_eru_routes_url}/processo/func/remove_dashboard_tab`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  async resetState() {
    let currentDashboardId = sessionStorage.getItem('current_dashboard_id');
    
    // Preserve pending entity filters before resetting
    const preservedPendingFilters = new Map(this.pendingEntityFilters);
    //console.log('[OrgDashboardService] Preserving pending filters during resetState:', Array.from(preservedPendingFilters.keys()));
    
    this.currentDashBoard.set(null);
    this.selectedTab.set(1);
    this.filterStateMap.clear();
    this.lastEntityKey = null;
    this.openWidgetSlider = false;
    this.drilldownKey = null;
    dashboardCache.clear();
    this.resetConfig();  
    this.updateFilterState([]);
    
    // Restore pending entity filters after reset
    this.pendingEntityFilters = preservedPendingFilters;
    //console.log('[OrgDashboardService] Restored pending filters after resetState:', Array.from(this.pendingEntityFilters.keys()));
    
    this.setDashboardContext(false);
    try {
      // Clear dashboard-related caches when resetting dashboard
      const { dashboard_filter, dashboardCache } = await import('@processo/shared/components/entity-layout/smart-table/cache');
      dashboard_filter.clear();
      dashboardCache.clear();
    } catch {}
  }

  getLastSavedFiltersFromStorage(): { meta: any, filters: any[] } | null {
    try {
      const raw = sessionStorage.getItem('dbf::last');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return { meta: parsed?.meta || null, filters: parsed?.filters || [] };
    } catch {
      return null;
    }
  }

  filterSchema(filters:any){
    const mappedFilters=filters?.map((_filter: any) => {
      if (_filter.type === 'date_range') {
        // ✅ FIX: Convert string placeholders to actual dates
        if (Array.isArray(_filter.default) && _filter.default.includes('FIRST_DAY')) {
          return {
            ..._filter,
            default: [
              moment().startOf('month').toDate(),
              moment().endOf('month').toDate(),
            ],
          };
        } else if (_filter.default === 'Current_Date') {
          return {
            ..._filter,
            default: [
              moment().startOf('day').toDate(),
              moment().endOf('day').toDate(),
            ],
          };
        } else if (_filter.default == null) {
          return { ..._filter, default: [] };
        }
      } else if (_filter.type === 'date') {
        if (_filter.default === 'Current_Date') {
          return { ..._filter, default: moment().toDate() };
        } else if (_filter.default == null) {
          return { ..._filter, default: '' };
        }
      } else if (_filter.type === 'dropdown_multi_select' && _filter.default === '') {
        return { ..._filter, default: [] };
      }
      return _filter; // Return other filters as is
    });
    return mappedFilters;
  }
  
  
  getFilterJsonForApiCall() {
    if (!this.isInDashboardContext()) {
      return {};
    }
  
    let data: any = {};
    const currentDashboard = this.currentDashBoard();
    if (!currentDashboard) {
      return {};
    }
  
    const dashboardKey = `${currentDashboard?.org_id}_${currentDashboard?.process_id}_${currentDashboard?.org_process_did}`;
    const storedFilters = this.getStoredFilterState(dashboardKey);
    const filtersToUse = storedFilters.length > 0 ? storedFilters : this.filterSchema(currentDashboard?.dashboard?.filters);
  
    filtersToUse?.forEach((_field: any) => {
      // ✅ Check if this field depends on another field (has 'df' property)
      // if (_field.df) {
      //   const parentFilter = filtersToUse.find((f: any) => f.name === _field.df);
      //   if (parentFilter && this.isEmptyValue(parentFilter.default)) {
      //     //console.log(`⏭️ Skipping ${_field.name} because parent ${_field.df} is empty`);
      //     return; // Skip this dependent filter
      //   }
      // }
  
      // ✅ Only include filters with meaningful values
      if (_field && this.hasValidValue(_field)) {
        if (_field.type === 'date') {
          let format_date = moment(_field.default).format(_field.format || 'YYYY-MM-DD');
          data[`${_field.name}`] = format_date;
        } else if (_field.type === 'date_range') {
          if (Array.isArray(_field.default) && _field.default.length === 2) {
            let start_date = moment(_field.default[0]).format(_field.format || 'YYYY-MM-DD');
            let end_date = moment(_field.default[1]).format(_field.format || 'YYYY-MM-DD');
            data[`from_${_field.name}`] = start_date;
            data[`to_${_field.name}`] = end_date;
          }
        } else if (_field.type === 'dropdown_multi_select') {
          if (Array.isArray(_field.default) && _field.default.length > 0) {
            //console.log(`[DashboardService] Creating filter for ${_field.name}:`, _field.default);
            data[`${_field.name}`] = { $in: _field.default };
           // console.log(`[DashboardService] Result filter:`, data[`${_field.name}`]);
          }
        } else if (_field?.type === 'checkbox') {
          if (_field.default === true || _field.default === false) {
            data[`${_field.name}`] = _field.default;
          }
        } else {
          data[`${_field.name}`] = _field.default;
        }
      }
    });
    
    //console.log(`[DashboardService] Final filter data:`, data);
    return data;
  };
  
  // ✅ Helper method to check if value is truly empty
  private isEmptyValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    return false;
  }
  
  // ✅ Helper method to check if field has valid value
  private hasValidValue(field: any): boolean {
    if (this.isEmptyValue(field.default)) {
      return false;
    }
    
    // ✅ For date objects, check if they're valid dates
    if (field.type === 'date' && field.default instanceof Date) {
      return !isNaN(field.default.getTime());
    }
    
    // ✅ For date range, check if it's an array with 2 valid dates
    if (field.type === 'date_range' && Array.isArray(field.default)) {
      return field.default.length === 2 && 
             field.default.every((date: any) => date instanceof Date && !isNaN(date.getTime()));
    }
    
    return true;
  }
  
  setDrilldownContext(sourceKey: string) {
    this.drilldownKey = sourceKey;
  }

  refreshWidgets= new Subject<void>()


  /**
   * Update dashboard filters from external source (e.g., entity filters)
   * This enables bidirectional filter synchronization
   */
  updateDashboardFiltersFromExternal(externalFilters: any[]): void {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard?.dashboard?.filters) {
        console.warn('[OrgDashboardService] No dashboard context available for external filter update');
        return;
      }

      // Check if we have valid dashboard context or if we can still update filters
      // Allow updates even if not in dashboard context, as long as we have dashboard data
      const hasValidDashboard = currentDashboard && 
                                currentDashboard.dashboard && 
                                currentDashboard.dashboard.filters && 
                                Array.isArray(currentDashboard.dashboard.filters);

      if (!hasValidDashboard) {
        console.warn('[OrgDashboardService] No valid dashboard data available for external filter update');
        return;
      }

      // Merge external filters with existing dashboard filters
      const existingFilters = currentDashboard.dashboard.filters;
      const mergedFilters = this.mergeExternalFiltersWithDashboard(existingFilters, externalFilters);
      
      // Update dashboard state
      this.currentDashBoard.update((prev: any) => {
        if (prev) {
          return {
            ...prev,
            dashboard: {
              ...prev.dashboard,
              filters: mergedFilters
            }
          };
        }
        return prev;
      });

      // Update internal filter state
      this.updateFilterState(mergedFilters);
      
      // Store the updated state
      const dashboardKey = `${currentDashboard.org_id}_${currentDashboard.process_id}_${currentDashboard.org_process_did}`;
      this.storeFilterState(dashboardKey, mergedFilters);
      
      // Trigger widget refresh only if we're actually in dashboard context
      if (this.isInDashboardContext()) {
        setTimeout(() => {
          this.refreshWidgets.next();
        }, 100);
      }
      
     // console.log('[OrgDashboardService] Updated dashboard filters from external source:', externalFilters);
    } catch (error) {
      console.error('[OrgDashboardService] Error updating dashboard filters from external source:', error);
    }
  }

  /**
   * Merge external filters with existing dashboard filters
   */
  private mergeExternalFiltersWithDashboard(existingFilters: any[], externalFilters: any[]): any[] {
    const result = [...existingFilters];
    //console.log(`[OrgDashboardService] Merging external filters:`, externalFilters);
    //console.log(`[OrgDashboardService] With existing filters:`, existingFilters.map(f => ({ name: f.name, key: f.key, default: f.default })));
    
    for (const externalFilter of externalFilters) {
      //console.log(`[OrgDashboardService] Processing external filter:`, { name: externalFilter.name, key: externalFilter.key, default: externalFilter.default });
      
      const existingIndex = result.findIndex((f: any) => {
        // Prioritize name matching over key matching
        if (externalFilter.name && f.name === externalFilter.name) {
          return true;
        }
        if (externalFilter.key && f.key === externalFilter.key) {
          return true;
        }
        return false;
      });
      
      //console.log(`[OrgDashboardService] Found existing filter at index:`, existingIndex);
      
      if (existingIndex >= 0) {
        // Update existing filter
        //console.log(`[OrgDashboardService] Updating existing filter at index ${existingIndex}:`, result[existingIndex]);
        result[existingIndex] = {
          ...result[existingIndex],
          ...externalFilter
        };
        //console.log(`[OrgDashboardService] Updated filter:`, result[existingIndex]);
      } else {
        // Add new filter if it has a valid schema
        if (externalFilter.name || externalFilter.key) {
          //console.log(`[OrgDashboardService] Adding new filter:`, externalFilter);
          result.push(externalFilter);
        } else {
          //console.log(`[OrgDashboardService] Skipping external filter - no name or key`);
        }
      }

      
    }
    
    //console.log(`[OrgDashboardService] Final merged result:`, result.map(f => ({ name: f.name, key: f.key, default: f.default })));
    return result;
  }

  /**
   * Check if a filter exists in the current dashboard
   */
  hasFilter(filterName: string): boolean {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard?.dashboard?.filters) {
        return false;
      }
      
      return currentDashboard.dashboard.filters.some((f: any) => 
        f.name === filterName || f.key === filterName
      );
    } catch (error) {
      console.error('[OrgDashboardService] Error checking filter existence:', error);
      return false;
    }
  }

  /**
   * Get filter by name from current dashboard
   */
  getFilterByName(filterName: string): any {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard?.dashboard?.filters) {
        return null;
      }
      
      return currentDashboard.dashboard.filters.find((f: any) => 
        f.name === filterName || f.key === filterName
      );
    } catch (error) {
      console.error('[OrgDashboardService] Error getting filter by name:', error);
      return null;
    }
  }

  /**
   * Check if there's valid dashboard data available for filter operations
   * This is useful when not in dashboard context but need to sync filters
   */
  hasValidDashboardData(): boolean {
    try {
      const currentDashboard = this.currentDashBoard();
      return !!(currentDashboard && 
                currentDashboard.dashboard && 
                currentDashboard.dashboard.filters && 
                Array.isArray(currentDashboard.dashboard.filters));
    } catch (error) {
      console.error('[OrgDashboardService] Error checking dashboard data validity:', error);
      return false;
    }
  }

  /**
   * Get dashboard key for current dashboard
   */
  getCurrentDashboardKey(): string | null {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard) {
        return null;
      }
      return `${currentDashboard.org_id}_${currentDashboard.process_id}_${currentDashboard.org_process_did}`;
    } catch (error) {
      console.error('[OrgDashboardService] Error getting dashboard key:', error);
      return null;
    }
  }

  /**
   * Store entity filters that will be applied when dashboard loads
   */
  storePendingEntityFilters(dashboardKey: string, filters: any[]): void {
    try {
      this.pendingEntityFilters.set(dashboardKey, filters);
      //console.log('[OrgDashboardService] Stored pending entity filters for dashboard:', dashboardKey, filters);
      //console.log('[OrgDashboardService] All pending filter keys:', Array.from(this.pendingEntityFilters.keys()));
    } catch (error) {
      console.error('[OrgDashboardService] Error storing pending entity filters:', error);
    }
  }

  /**
   * Get pending entity filters for a dashboard
   */
  getPendingEntityFilters(dashboardKey: string): any[] {
    try {
      return this.pendingEntityFilters.get(dashboardKey) || [];
    } catch (error) {
      console.error('[OrgDashboardService] Error getting pending entity filters:', error);
      return [];
    }
  }

  /**
   * Clear pending entity filters for a dashboard
   */
  clearPendingEntityFilters(dashboardKey: string): void {
    try {
      this.pendingEntityFilters.delete(dashboardKey);
      //console.log('[OrgDashboardService] Cleared pending entity filters for dashboard:', dashboardKey);
    } catch (error) {
      console.error('[OrgDashboardService] Error clearing pending entity filters:', error);
    }
  }

  /**
   * Apply pending entity filters when dashboard loads
   */
  applyPendingEntityFilters(dashboardKey: string): void {
    try {
      //console.log('[OrgDashboardService] Checking for pending filters for dashboard key:', dashboardKey);
     // console.log('[OrgDashboardService] All available pending filter keys:', Array.from(this.pendingEntityFilters.keys()));
      const pendingFilters = this.getPendingEntityFilters(dashboardKey);
      //console.log('[OrgDashboardService] Found pending filters:', pendingFilters);
      
      if (pendingFilters.length > 0) {
       // console.log('[OrgDashboardService] Applying pending entity filters:', pendingFilters);
        this.updateDashboardFiltersFromExternal(pendingFilters);
        this.clearPendingEntityFilters(dashboardKey);
       // console.log('[OrgDashboardService] Successfully applied and cleared pending filters');
      } else {
       // console.log('[OrgDashboardService] No pending filters found for dashboard key:', dashboardKey);
        // Try to find similar keys
        const similarKeys = Array.from(this.pendingEntityFilters.keys()).filter(key => 
          key.includes(dashboardKey.split('_')[0]) && key.includes(dashboardKey.split('_')[1])
        );
        if (similarKeys.length > 0) {
         // console.log('[OrgDashboardService] Found similar keys that might match:', similarKeys);
        }
      }
    } catch (error) {
      console.error('[OrgDashboardService] Error applying pending entity filters:', error);
    }
  }

  export_excel(excel_styles: any, pivot_config: any, columns: any, query_name: string) {
    var body: any = {
      excel_styles,
      pivot_config,
      columns,
      filter: this.getFilterJsonForApiCall(),
      org_id: this.currentDashBoard()?.org_id, 
      process_id: this.currentDashBoard()?.process_id, 
    }
    return this.http.post(`${environment.base_url}/store/processo/myquery/execute/${query_name}/excel`, body, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map(response => {
        const contentType = response.headers.get('content-type');
        
        if (contentType && (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || 
                           contentType.includes('application/vnd.ms-excel'))) {
          // Create blob and trigger download
          const blob = new Blob([response.body!], { type: contentType });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${query_name}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
        
        return response;
      })
    );
  }



}